import { useEffect, useState, type CSSProperties, type SVGProps } from 'react';
import { usePage } from '@inertiajs/react';
import posthog from 'posthog-js';
import { ArrowRight } from 'lucide-react';

import { PUBLIC_DEMO_CTA_CLICKED } from '@/components/public/PublicDemoLink';
import { BlogSection } from '@/components/public/sections/BlogSection';
import { DemoRequestForm } from '@/components/public/DemoRequestForm';
import { InteractionExampleCard } from '@/components/public/InteractionExampleCard';
import { motion } from '@/components/public/PublicMotion';
import { PenUnderline } from '@/components/public/PenUnderline';
import { PublicLayout } from '@/components/public/PublicLayout';
import { PublicSeo } from '@/components/public/PublicSeo';
import { SectionEyebrow } from '@/components/public/SectionEyebrow';
import { WeeklySummaryCard } from '@/components/public/WeeklySummaryCard';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Heading } from '@/components/ui/heading';
import {
  fadeInUp,
  listReveal,
  revealTransition,
  scaleIn,
  sectionReveal,
  staggerContainer,
} from '@/hooks/use-scroll-animation';

// ─── Icone a tratto di penna (motivo grafico del brand) ────────────────────

const penIconProps = {
  viewBox: '0 0 48 48',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: 2.5,
  strokeLinecap: 'round',
  strokeLinejoin: 'round',
  'aria-hidden': true,
} satisfies SVGProps<SVGSVGElement>;

const MemoryFadeIcon = ({ className }: { className?: string }) => (
  <svg {...penIconProps} className={className}>
    <path d="M8 16 C 14 12, 20 20, 26 16 S 36 12, 40 16" />
    <path d="M8 25 C 14 21, 20 29, 26 25 S 36 21, 40 25" opacity={0.55} />
    <path d="M8 34 C 14 30, 20 38, 26 34 S 36 30, 40 34" opacity={0.25} />
  </svg>
);

const ClockIcon = ({ className }: { className?: string }) => (
  <svg {...penIconProps} className={className}>
    <circle cx="24" cy="24" r="15" />
    <path d="M24 15 V24 L31 28" />
  </svg>
);

const ChatRiskIcon = ({ className }: { className?: string }) => (
  <svg {...penIconProps} className={className}>
    <rect x="9" y="9" width="30" height="22" rx="6" />
    <path d="M16 31 L12 39 L22 31" />
    <path d="M24 15 V23" />
    <circle cx="24" cy="27" r="1.4" fill="currentColor" stroke="none" />
  </svg>
);

const QuestionIcon = ({ className }: { className?: string }) => (
  <svg {...penIconProps} className={className}>
    <path d="M17 19 a7 7 0 1 1 10 6.5 c-2.5 1.2 -3 2.5 -3 5" />
    <circle cx="24" cy="37" r="1.6" fill="currentColor" stroke="none" />
  </svg>
);

const ScalesIcon = ({ className }: { className?: string }) => (
  <svg {...penIconProps} className={className}>
    <path d="M24 10 V38" />
    <path d="M18 38 H30" />
    <path d="M12 14 H36" />
    <path d="M12 14 V21" />
    <path d="M36 14 V21" />
    <path d="M6 21 a6 5 0 0 0 12 0" />
    <path d="M30 21 a6 5 0 0 0 12 0" />
  </svg>
);

const ShieldCheckIcon = ({ className }: { className?: string }) => (
  <svg {...penIconProps} className={className}>
    <path d="M24 8 L38 13 V23 C38 32 32 37 24 40 C16 37 10 32 10 23 V13 Z" />
    <path d="M18 24 L22 28 L30 19" />
  </svg>
);

const CrossedCircleIcon = ({ className }: { className?: string }) => (
  <svg {...penIconProps} className={className}>
    <circle cx="24" cy="24" r="15" />
    <path d="M14 14 L34 34" />
  </svg>
);

// ─── Contenuti ──────────────────────────────────────────────────────────────

const problemCards = [
  {
    number: '01',
    icon: MemoryFadeIcon,
    title: 'La memoria rielabora',
    body: 'In seduta la persona racconta martedì con lo stato emotivo di oggi. L’episodio originale è già stato filtrato e reinterpretato.',
  },
  {
    number: '02',
    icon: ClockIcon,
    title: '15–20 minuti per ricostruire',
    body: 'I primi minuti di ogni colloquio se ne vanno a rincorrere i fatti: lavoro di memoria, non lavoro clinico. Su 20 clienti, ~5 ore a settimana.',
  },
  {
    number: '03',
    icon: ChatRiskIcon,
    title: 'Il vuoto viene riempito male',
    body: '~37% degli adulti usa già chatbot generalisti per supporto emotivo. Accondiscendenti, senza supervisione, fuori dal controllo del professionista.',
  },
] as const;

const genericAiTraits = [
  'Dà risposte e consigli',
  'Consola e asseconda',
  'Nessuna supervisione clinica',
  'Fuori dal controllo del professionista',
] as const;

const interrogativeAiTraits = [
  'Pone solo domande maieutiche',
  'Neutrale: non giudica, non interpreta',
  'Ogni interazione è supervisionata',
  'Dentro un percorso governato da te',
] as const;

const iaiPrinciples = [
  {
    icon: QuestionIcon,
    title: 'Maieutica',
    body: 'Domande mirate, legate a ciò che la persona scrive: chiariscono episodio, contesto e segnali.',
  },
  {
    icon: ScalesIcon,
    title: 'Neutrale',
    body: 'Non consola, non consiglia, non diagnostica. Mai.',
  },
  {
    icon: ShieldCheckIcon,
    title: 'Supervisionata',
    body: 'Ogni interazione passa da un livello di controllo automatico, sempre visibile a te.',
  },
] as const;

const materialBullets = [
  {
    title: 'Riassunto del periodo.',
    body: 'Una sintesi ordinata, sull’arco di tempo che scegli tu.',
  },
  {
    title: 'Temi ricorrenti e osservazioni.',
    body: 'Pattern che a occhio nudo richiederebbero mesi per emergere.',
  },
  {
    title: 'Eventi chiave, con i dati grezzi.',
    body: 'Puoi sempre risalire a chat e diari integrali, per verificare.',
  },
] as const;

const summaryThemes = ['Timore del giudizio', 'Sonno irregolare', 'Regolazione emotiva'] as const;

const summaryStats = [
  { value: 4, label: 'diari completati' },
  { value: 4, label: 'conversazioni' },
  { value: 2, label: 'note' },
] as const;

const safetySteps = [
  {
    number: '01',
    title: 'Supervisione in tempo reale',
    body: 'Ogni interazione — della persona e dell’IA — è valutata da un sistema automatico che cerca segnali di rischio.',
  },
  {
    number: '02',
    title: 'Notifica al Safety Team',
    body: 'Se rileva un potenziale pericolo — ideazione suicidaria, crisi grave — avvisa il Safety Team di Emovia, che valuta l’intervento.',
  },
  {
    number: '03',
    title: 'Blocco immediato della chat',
    body: 'Come misura ulteriore, il sistema blocca subito la conversazione della persona.',
  },
  {
    number: '04',
    title: 'La situazione torna a te',
    body: 'Lo psicologo viene notificato e riprende in mano la situazione nel canale clinico appropriato.',
  },
] as const;

const privacyChips = [
  'Conforme GDPR',
  'Server in Europa',
  'Dati crittografati',
  'Nessun training sui dati',
  'Cancellazione entro 30 giorni',
  'Segreto professionale',
] as const;

const perimeterItems = [
  {
    title: 'Non è uno psicologo virtuale',
    body: 'Non fa terapia, non prende decisioni cliniche, non formula diagnosi.',
  },
  {
    title: 'Non è un chatbot che consiglia',
    body: 'Non consola, non suggerisce cosa fare, non simula amicizia.',
  },
  {
    title: 'Non è un’IA generalista',
    body: 'Ha un compito preciso e un profilo per ogni cliente, definito da te.',
  },
  {
    title: 'Non lavora fuori dal percorso',
    body: 'Esiste solo dentro una relazione terapeutica governata dal professionista.',
  },
] as const;

const demoPoints = [
  'Vedi che materiale arriva davvero, nella forma in cui arriva.',
  'Capisci dove l’IA Interrogativa fa domande, dove si ferma e perché.',
  'Valuti perimetro, accessi e privacy senza ambiguità.',
] as const;

type LatestPost = {
  id: number;
  title: string;
  slug: string;
  caption?: string | null;
  publishedAt?: string | null;
  ogImage?: { url: string } | null;
};

type PageProps = {
  latestPosts?: LatestPost[];
};

// ─── Hero: scambio diario ↔ IA Interrogativa animato a step ────────────────

const heroExchange = [
  {
    speaker: 'persona',
    text: '«Stanotte ho dormito poco, continuavo a pensare alla riunione di domani.»',
  },
  { speaker: 'emovia', text: 'Che cosa della riunione ti preoccupava di più?' },
  { speaker: 'persona', text: '«Il giudizio. L’idea che ogni mia parola venga pesata.»' },
  {
    speaker: 'emovia',
    text: 'È una sensazione comparsa stasera, o ti accompagnava già durante la giornata?',
  },
] as const;

// 4 battute + card riepilogo, poi qualche beat di pausa prima di ripartire.
const HERO_LOOP_STEPS = 9;
const HERO_STEP_MS = 1400;

const HeroExchange = () => {
  // Parte con tutto visibile: niente flash SSR e contenuto leggibile senza JS.
  const [step, setStep] = useState(HERO_LOOP_STEPS - 1);

  useEffect(() => {
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const timer = setInterval(() => setStep((s) => (s + 1) % HERO_LOOP_STEPS), HERO_STEP_MS);
    return () => clearInterval(timer);
  }, []);

  const beatStyle = (index: number): CSSProperties => ({
    opacity: step > index ? 1 : 0,
    transform: step > index ? 'translateY(0)' : 'translateY(10px)',
    transition: 'opacity 0.5s cubic-bezier(0.4,0,0.2,1), transform 0.5s cubic-bezier(0.4,0,0.2,1)',
  });

  return (
    <div className="relative">
      <svg
        viewBox="0 0 420 420"
        className="absolute -inset-10 h-[calc(100%+80px)] w-[calc(100%+80px)] text-blue-accent"
        aria-hidden
      >
        <path
          d="M30 330 C 80 80, 320 60, 390 180"
          fill="none"
          stroke="currentColor"
          strokeWidth={3}
          strokeLinecap="round"
          strokeDasharray="6 12"
          opacity={0.8}
        />
      </svg>

      <div className="relative z-10 grid gap-3.5 rounded-3xl border border-border bg-card p-7 shadow-card">
        <div className="flex items-baseline justify-between gap-3">
          <p className="font-accent text-xs uppercase tracking-wide text-muted-foreground">
            La persona scrive nel diario
          </p>
          <span className="text-xs text-muted-foreground">lun · 22:14</span>
        </div>

        {heroExchange.map((beat, index) =>
          beat.speaker === 'persona' ? (
            <div
              key={`${beat.speaker}-${index}`}
              style={beatStyle(index)}
              className="max-w-[92%] rounded-2xl rounded-bl-sm bg-secondary px-4 py-3.5 text-[0.95rem] leading-relaxed text-secondary-foreground"
            >
              {beat.text}
            </div>
          ) : (
            <div
              key={`${beat.speaker}-${index}`}
              style={beatStyle(index)}
              className="flex justify-end gap-2.5"
            >
              <div className="max-w-[86%] rounded-2xl rounded-br-sm bg-blue-light px-4 py-3.5 text-[0.95rem] leading-relaxed text-primary-dark">
                {beat.text}
              </div>
              <div
                aria-hidden
                className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-primary text-[0.7rem] font-bold text-primary-foreground"
              >
                EM
              </div>
            </div>
          ),
        )}
      </div>

      <div
        style={beatStyle(4)}
        className="relative z-20 -mt-4 ml-10 flex items-center gap-3.5 rounded-xl border border-border bg-card px-5 py-4 shadow-lg"
      >
        <span aria-hidden className="h-2.5 w-2.5 shrink-0 rounded-full bg-green" />
        <div>
          <p className="text-sm font-semibold text-primary-dark">Per te, prima della seduta</p>
          <p className="text-xs text-muted-foreground">
            Episodio fissato: contesto, emozione, pensiero
          </p>
        </div>
      </div>
    </div>
  );
};

// ─── Pagina ─────────────────────────────────────────────────────────────────

const NewHome = () => {
  const { props } = usePage<PageProps>();
  const { latestPosts } = props;

  return (
    <PublicLayout>
      <PublicSeo
        title="Emovia | IA per psicologi e psicoterapeuti"
        description="Emovia raccoglie ciò che la persona vive tra le sedute e lo restituisce a psicologi e psicoterapeuti come materiale clinico ordinato. Richiedi una demo."
        path="/"
      />

      {/* Hero */}
      <section className="relative overflow-hidden bg-background">
        <div className="container grid gap-12 py-20 lg:grid-cols-[1.05fr_0.95fr] lg:items-center lg:gap-16 lg:py-24">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={staggerContainer}
            className="grid gap-6"
          >
            <motion.div variants={fadeInUp} transition={revealTransition}>
              <SectionEyebrow className="mb-5 text-primary">
                Per psicologi e psicoterapeuti
              </SectionEyebrow>
              <Heading level={1} font="display" className="text-4xl text-primary-dark md:text-6xl">
                Più materiale clinico utile, prima della <PenUnderline>seduta.</PenUnderline>
              </Heading>
            </motion.div>

            <motion.p
              variants={fadeInUp}
              transition={revealTransition}
              className="max-w-[52ch] text-lg text-gray-darker md:text-xl"
            >
              La persona scrive nel diario, l’<b>IA Interrogativa</b> risponde solo con domande che
              mettono a fuoco. Tu ricevi materiale leggibile da riprendere in colloquio. Non
              interpreta al posto tuo.
            </motion.p>

            <motion.div
              variants={fadeInUp}
              transition={revealTransition}
              className="flex flex-col gap-4 sm:flex-row sm:items-center"
            >
              <Button size="lg" asChild className="group h-13 px-7 text-base">
                <a
                  href="#demo-form"
                  onClick={() =>
                    posthog.capture(PUBLIC_DEMO_CTA_CLICKED, {
                      cta_location: 'newhome_hero_anchor',
                      destination_path: '#demo-form',
                    })
                  }
                >
                  Richiedi una demo
                  <ArrowRight className="ml-2 h-5 w-5 transition-transform duration-200 group-hover:translate-x-1" />
                </a>
              </Button>
              <a
                href="#materiale"
                className="border-b-2 border-blue-accent pb-0.5 text-base font-semibold text-primary no-underline hover:text-primary-dark"
              >
                Guarda che cosa ti arriva
              </a>
            </motion.div>

            <motion.p
              variants={fadeInUp}
              transition={revealTransition}
              className="text-sm text-muted-foreground"
            >
              Co-fondato da una psicologa. Progettato con psicologi, per psicologi.
            </motion.p>
          </motion.div>

          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeInUp}
            transition={{ ...revealTransition, delay: 0.2 }}
            className="hidden lg:block"
          >
            <HeroExchange />
          </motion.div>
        </div>
      </section>

      {/* Il problema */}
      <section id="problema" className="bg-secondary">
        <div className="container py-20 md:py-24">
          <motion.div {...sectionReveal} className="grid max-w-[720px] gap-3.5">
            <SectionEyebrow className="text-orange-dark">Il problema</SectionEyebrow>
            <Heading level={2} className="text-3xl font-bold text-primary-dark md:text-4xl">
              Il vuoto tra le sedute si paga in seduta.
            </Heading>
            <p className="text-lg text-gray-darker">
              Le cose clinicamente più rilevanti accadono fuori dallo studio. Ma arrivano in
              colloquio degradate — per tre ragioni strutturali.
            </p>
          </motion.div>

          <motion.div
            {...sectionReveal}
            className="my-12 rounded-3xl border border-orange-dark/10 bg-white/65 px-6 py-7 md:px-8"
          >
            <div className="flex items-center">
              <div className="grid shrink-0 justify-items-center gap-1.5">
                <span aria-hidden className="h-4 w-4 rounded-full bg-primary" />
                <span className="text-xs font-bold text-primary-dark">Seduta</span>
              </div>
              <div className="relative mx-3 h-14 flex-1">
                <svg
                  viewBox="0 0 600 56"
                  preserveAspectRatio="none"
                  className="absolute inset-0 h-full w-full text-orange"
                  aria-hidden
                >
                  <path
                    d="M0 20 C 60 10, 100 32, 160 24 S 280 8, 360 26 S 520 34, 600 20"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth={2.5}
                    strokeLinecap="round"
                    strokeDasharray="2 8"
                    opacity={0.9}
                  />
                </svg>
                <span className="absolute left-[14%] top-8 text-xs text-orange-dark">episodio</span>
                <span className="absolute left-[42%] top-0 text-xs text-orange-dark">
                  notte insonne
                </span>
                <span className="absolute left-[68%] top-9 text-xs text-orange-dark">
                  pensiero ricorrente
                </span>
              </div>
              <div className="grid shrink-0 justify-items-center gap-1.5">
                <span aria-hidden className="h-4 w-4 rounded-full bg-primary" />
                <span className="text-xs font-bold text-primary-dark">Seduta</span>
              </div>
            </div>
            <p className="mt-3.5 text-center text-sm text-muted-foreground">
              Sette giorni di vissuto, ricostruiti a memoria in pochi minuti di colloquio.
            </p>
          </motion.div>

          <motion.div {...listReveal} className="grid gap-6 md:grid-cols-3">
            {problemCards.map((card) => (
              <motion.div
                key={card.number}
                variants={fadeInUp}
                transition={revealTransition}
                className="rounded-lg border border-border bg-card p-7 shadow-card"
              >
                <div className="mb-3.5 flex items-start justify-between">
                  <span className="font-accent text-sm font-bold text-orange">{card.number}</span>
                  <card.icon className="h-10 w-10 text-orange" />
                </div>
                <Heading level={3} className="text-primary-dark">
                  {card.title}
                </Heading>
                <p className="mt-2 text-[0.95rem] text-gray-darker">{card.body}</p>
              </motion.div>
            ))}
          </motion.div>

          <motion.p
            {...sectionReveal}
            className="mt-12 max-w-[760px] text-xl font-semibold text-primary-dark"
          >
            Il problema non è il cliente: è il vuoto che si lascia tra una seduta e l’altra. Se
            interveniamo lì, non stiamo aggiungendo tecnologia — stiamo recuperando terapia.
          </motion.p>
        </div>
      </section>

      {/* IA Interrogativa */}
      <section id="iai" className="bg-primary-dark text-white">
        <div className="container py-20 md:py-28">
          <motion.div {...sectionReveal} className="grid max-w-[760px] gap-3.5">
            <SectionEyebrow className="text-blue-accent">IA Interrogativa</SectionEyebrow>
            <Heading level={2} className="text-3xl font-bold text-white md:text-4xl">
              Le IA generative danno risposte.
              <br />
              La nostra è progettata per fare domande.
            </Heading>
            <p className="max-w-[60ch] text-lg text-blue-light">
              Fa in piccolo, tra le sedute, ciò che fai tu in studio: stimola l’introspezione con
              domande mirate. La risposta resta sempre della persona. Lo spazio interpretativo resta
              interamente tuo.
            </p>
          </motion.div>

          <div className="mt-16 grid items-start gap-12 lg:grid-cols-2">
            <div className="grid gap-5">
              <motion.div {...sectionReveal} className="grid gap-4 sm:grid-cols-2">
                <div className="rounded-lg border border-white/15 bg-white/7 p-6">
                  <p className="mb-3.5 font-accent text-xs uppercase tracking-wide text-white/55">
                    IA generalista
                  </p>
                  <ul className="grid gap-2.5 text-[0.92rem] text-white/75">
                    {genericAiTraits.map((trait) => (
                      <li key={trait}>{trait}</li>
                    ))}
                  </ul>
                </div>
                <div className="rounded-lg bg-white p-6 shadow-lg">
                  <p className="mb-3.5 font-accent text-xs uppercase tracking-wide text-primary">
                    IA Interrogativa
                  </p>
                  <ul className="grid gap-2.5 text-[0.92rem] font-medium text-primary-dark">
                    {interrogativeAiTraits.map((trait) => (
                      <li key={trait}>{trait}</li>
                    ))}
                  </ul>
                </div>
              </motion.div>

              <motion.div {...listReveal} className="grid gap-6 sm:grid-cols-3 sm:gap-4">
                {iaiPrinciples.map((principle) => (
                  <motion.div
                    key={principle.title}
                    variants={fadeInUp}
                    transition={revealTransition}
                    className="border-t-2 border-blue-accent pt-3.5"
                  >
                    <principle.icon className="mb-2.5 h-9 w-9 text-blue-accent" />
                    <p className="mb-1.5 font-bold">{principle.title}</p>
                    <p className="text-sm leading-relaxed text-blue-light">{principle.body}</p>
                  </motion.div>
                ))}
              </motion.div>

              <motion.p
                {...sectionReveal}
                className="border-l-[3px] border-blue-accent pl-4 text-blue-light"
              >
                È calibrata sul singolo cliente, dal profilo che definisci tu: da «la nostra IA per
                te» a «la tua IA».
              </motion.p>
            </div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.3 }}
              variants={scaleIn}
              transition={{ duration: 0.5 }}
              className="rounded-3xl bg-white shadow-lg"
            >
              <InteractionExampleCard footer="L’IA fa ordine nella raccolta del materiale. Il senso di quel materiale lo costruisci tu, in seduta." />
            </motion.div>
          </div>
        </div>
      </section>

      {/* La trasformazione */}
      <section className="bg-background">
        <div className="container max-w-[880px] py-20 text-center md:py-28">
          <motion.div {...sectionReveal} className="mb-10">
            <SectionEyebrow className="text-primary" align="center">
              Che cosa cambia nel materiale
            </SectionEyebrow>
          </motion.div>

          <motion.p
            {...sectionReveal}
            className="mx-auto text-xl italic text-muted-foreground md:text-2xl"
          >
            «È stata una settimana stressante.»
          </motion.p>

          <motion.div {...sectionReveal} className="my-5 flex justify-center">
            <svg viewBox="0 0 60 64" className="h-12 w-11 overflow-visible text-orange" aria-hidden>
              <path
                d="M30 4 C 22 20, 40 32, 30 52"
                fill="none"
                stroke="currentColor"
                strokeWidth={3}
                strokeLinecap="round"
                className="public-draw"
                style={
                  {
                    '--draw-length': '80',
                    '--draw-duration': '0.9s',
                    '--draw-delay': '0.3s',
                  } as CSSProperties
                }
              />
              <path
                d="M22 44 L 30 56 L 38 46"
                fill="none"
                stroke="currentColor"
                strokeWidth={3}
                strokeLinecap="round"
                strokeLinejoin="round"
                className="public-draw"
                style={
                  {
                    '--draw-length': '40',
                    '--draw-duration': '0.5s',
                    '--draw-delay': '1s',
                  } as CSSProperties
                }
              />
            </svg>
          </motion.div>

          <motion.p
            {...sectionReveal}
            className="mx-auto text-2xl font-semibold leading-[1.45] text-balance text-primary-dark md:text-3xl"
          >
            «Martedì,{' '}
            <span className="border-b-[3px] border-blue-accent pb-px">
              dopo la riunione con il capo
            </span>
            , ho sentito{' '}
            <span className="border-b-[3px] border-orange pb-px">un nodo allo stomaco</span> e ho
            pensato:{' '}
            <span className="border-b-[3px] border-green-accent pb-px">
              non sarò mai abbastanza
            </span>
            .»
          </motion.p>

          <motion.div
            {...sectionReveal}
            className="mt-7 flex flex-wrap justify-center gap-5 text-sm font-semibold text-gray-darker"
          >
            <span className="flex items-center gap-2">
              <span aria-hidden className="h-1 w-3.5 rounded-sm bg-blue-accent" />
              contesto
            </span>
            <span className="flex items-center gap-2">
              <span aria-hidden className="h-1 w-3.5 rounded-sm bg-orange" />
              emozione
            </span>
            <span className="flex items-center gap-2">
              <span aria-hidden className="h-1 w-3.5 rounded-sm bg-green-accent" />
              pensiero
            </span>
          </motion.div>

          <motion.p
            {...sectionReveal}
            className="mx-auto mt-9 max-w-[56ch] text-gray-darker md:text-lg"
          >
            Episodio, contesto, emozione, pensiero — fissati quando accadono, non ricostruiti a
            distanza di giorni.
          </motion.p>
        </div>
      </section>

      {/* Prima della seduta */}
      <section id="materiale" className="border-y border-border bg-gray-lighter">
        <div className="container grid gap-12 py-20 md:py-24 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:gap-16">
          <motion.div {...sectionReveal} className="grid gap-5">
            <SectionEyebrow className="text-primary">Prima della seduta</SectionEyebrow>
            <Heading level={2} className="text-3xl font-bold text-primary-dark md:text-4xl">
              Apri la dashboard e sai da dove partire.
            </Heading>
            <div className="grid gap-4">
              {materialBullets.map((bullet) => (
                <div key={bullet.title} className="flex gap-3.5">
                  <span aria-hidden className="mt-2 h-2 w-2 shrink-0 rounded-full bg-primary" />
                  <p className="max-w-none">
                    <strong>{bullet.title}</strong>{' '}
                    <span className="text-gray-darker">{bullet.body}</span>
                  </p>
                </div>
              ))}
            </div>
            <p className="text-lg font-semibold text-primary-dark">
              La domanda di apertura smette di essere «cosa è successo?» e diventa «perché è
              successo?».
            </p>
          </motion.div>

          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={scaleIn}
            transition={{ duration: 0.5 }}
            className="overflow-hidden rounded-3xl border border-border bg-card shadow-card"
          >
            <WeeklySummaryCard stats={summaryStats} themes={summaryThemes} />
          </motion.div>
        </div>
      </section>

      {/* Sicurezza clinica */}
      <section id="sicurezza" className="bg-blue-lighter">
        <div className="container py-20 md:py-28">
          <motion.div {...sectionReveal} className="grid max-w-[760px] gap-3.5">
            <SectionEyebrow className="text-primary">Sicurezza clinica</SectionEyebrow>
            <Heading level={2} className="text-3xl font-bold text-primary-dark md:text-4xl">
              Supervisionata per progetto, non per dichiarazione.
            </Heading>
            <p className="max-w-[62ch] text-lg text-gray-darker">
              È il principale differenziatore etico rispetto ai chatbot generici — e la risposta
              operativa al dovere di tutela (art. 13 del Codice Deontologico). Quando qualcosa va
              storto, succede questo:
            </p>
          </motion.div>

          <div className="relative mt-12">
            <svg
              viewBox="0 0 1100 40"
              preserveAspectRatio="none"
              className="absolute left-[6%] top-6 hidden h-10 w-[88%] text-primary lg:block"
              aria-hidden
            >
              <path
                d="M0 20 C 180 6, 320 32, 550 18 S 920 8, 1100 22"
                fill="none"
                stroke="currentColor"
                strokeWidth={2.5}
                strokeLinecap="round"
                strokeDasharray="3 9"
                opacity={0.5}
              />
            </svg>
            <motion.div
              {...listReveal}
              className="relative grid gap-5 md:grid-cols-2 lg:grid-cols-4"
            >
              {safetySteps.map((step) => (
                <motion.div
                  key={step.number}
                  variants={fadeInUp}
                  transition={revealTransition}
                  className="grid content-start gap-2.5 rounded-lg border border-border bg-card p-6 shadow-card"
                >
                  <span className="flex h-13 w-13 items-center justify-center rounded-full bg-primary font-accent font-bold text-primary-foreground">
                    {step.number}
                  </span>
                  <p className="font-bold text-primary-dark">{step.title}</p>
                  <p className="text-sm leading-relaxed text-gray-darker">{step.body}</p>
                </motion.div>
              ))}
            </motion.div>
          </div>

          <motion.p
            {...sectionReveal}
            className="mt-10 max-w-none text-lg font-semibold text-primary-dark"
          >
            I chatbot generalisti non hanno nulla di tutto questo: né supervisione, né alert, né un
            professionista da avvisare.
          </motion.p>

          <motion.div
            {...sectionReveal}
            className="mt-12 grid gap-4 rounded-3xl border border-border bg-card px-6 py-7 md:px-8"
          >
            <p className="font-accent text-xs uppercase tracking-wide text-muted-foreground">
              Privacy come architettura
            </p>
            <div className="flex flex-wrap gap-2.5">
              {privacyChips.map((chip) => (
                <span
                  key={chip}
                  className="rounded-full border border-border bg-gray-lighter px-4 py-2 text-sm font-semibold text-primary-dark"
                >
                  {chip}
                </span>
              ))}
            </div>
            <p className="max-w-none text-sm text-gray-darker">
              La persona decide cosa scrivere e cosa rendere visibile. Tu governi accessi, revoche
              ed eliminazione dei dati.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Perimetro */}
      <section className="border-b border-border bg-background">
        <div className="container py-16 md:py-20">
          <motion.div {...listReveal} className="grid gap-7 md:grid-cols-2 lg:grid-cols-4">
            {perimeterItems.map((item) => (
              <motion.div
                key={item.title}
                variants={fadeInUp}
                transition={revealTransition}
                className="border-t-2 border-magenta pt-4"
              >
                <CrossedCircleIcon className="mb-2.5 h-7 w-7 text-magenta opacity-85" />
                <p className="mb-1.5 font-bold text-primary-dark">{item.title}</p>
                <p className="text-sm leading-relaxed text-gray-darker">{item.body}</p>
              </motion.div>
            ))}
          </motion.div>
          <motion.p
            {...sectionReveal}
            className="mt-10 max-w-[70ch] text-lg font-semibold text-primary-dark"
          >
            Quello che è: uno strumento clinico che lavora <em>per</em> lo psicologo, mai{' '}
            <em>al posto</em> dello psicologo.
          </motion.p>
        </div>
      </section>

      {/* Quote co-founder */}
      <section className="bg-secondary">
        <div className="container grid max-w-[880px] gap-10 py-20 md:py-24 lg:grid-cols-[auto_1fr] lg:items-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.4 }}
            variants={scaleIn}
            transition={{ duration: 0.5 }}
            className="justify-self-center lg:justify-self-start"
          >
            <img
              src="https://cdn.emovia.com/img/team/elisa.webp"
              alt="Dott.ssa Elisa Manca"
              className="h-[150px] w-[150px] rounded-full object-cover"
              width={150}
              height={150}
            />
          </motion.div>
          <motion.figure {...sectionReveal} className="m-0 text-center lg:text-left">
            <blockquote className="text-xl font-semibold leading-[1.45] text-balance text-primary-dark md:text-2xl">
              «Due cose non negoziabili: utile davvero nel lavoro clinico, e protettivo verso chi lo
              usa. L’IA raccoglie e organizza; il senso clinico resta tuo. Nessuna interpretazione,
              nessuna scorciatoia.»
            </blockquote>
            <figcaption className="mt-4 text-[0.95rem] text-gray-darker">
              <strong>Dott.ssa Elisa Manca</strong> — Psicologa, co-founder di Emovia
            </figcaption>
          </motion.figure>
        </div>
      </section>

      <BlogSection posts={latestPosts} />

      {/* Resta connesso */}
      <section className="border-t border-border bg-background">
        <div className="container grid gap-12 py-20 md:py-24 lg:grid-cols-[0.9fr_1.1fr] lg:items-center lg:gap-16">
          <motion.div {...sectionReveal} className="grid gap-4">
            <Heading level={2} font="display" className="text-4xl text-primary-dark md:text-5xl">
              Resta connesso, anche tra le <PenUnderline animate={false}>sedute.</PenUnderline>
            </Heading>
            <p className="max-w-[46ch] text-lg text-gray-darker">
              Il ponte tra ciò che la persona vive ogni giorno e ciò che tu puoi riprendere in
              seduta.
            </p>
          </motion.div>
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, amount: 0.2 }}
            variants={scaleIn}
            transition={{ duration: 0.5 }}
          >
            <img
              src="https://cdn.emovia.com/seasky.webp"
              alt="Cielo e mare in luce diurna, fotografia naturale"
              className="h-64 w-full rounded-3xl object-cover md:h-[380px]"
              loading="lazy"
            />
          </motion.div>
        </div>
      </section>

      {/* Demo */}
      <section id="demo-form" className="bg-primary-dark">
        <div className="container grid gap-12 py-20 md:py-24 lg:grid-cols-2 lg:items-center lg:gap-16">
          <motion.div {...sectionReveal} className="grid gap-5 text-white">
            <SectionEyebrow className="text-blue-accent">Il passo successivo</SectionEyebrow>
            <Heading level={2} className="text-3xl font-bold text-white md:text-4xl">
              Vuoi vedere Emovia da vicino?
            </Heading>
            <div className="grid gap-3.5 text-blue-light">
              {demoPoints.map((point) => (
                <div key={point} className="flex gap-3">
                  <span aria-hidden className="font-bold text-blue-accent">
                    —
                  </span>
                  {point}
                </div>
              ))}
            </div>
            <p className="text-sm text-white/60">
              Senza giri di parole commerciali. Richiesta in pochi minuti.
            </p>
          </motion.div>

          <motion.div {...sectionReveal}>
            <Card className="rounded-3xl border-white/10 bg-card shadow-lg">
              <CardHeader className="space-y-2">
                <Heading level={3} className="text-primary-dark">
                  Richiedi una demo
                </Heading>
                <p className="text-sm text-gray-darker">
                  Compila il form. Ti contattiamo il prima possibile.
                </p>
              </CardHeader>
              <CardContent>
                <DemoRequestForm />
              </CardContent>
            </Card>
          </motion.div>
        </div>
      </section>
    </PublicLayout>
  );
};

export default NewHome;
